const asNodes = (value) => (Array.isArray(value) ? value : []);

const compareByOriginalPosition = (indexById, nodeById) => (leftId, rightId) => {
  const left = nodeById.get(leftId);
  const right = nodeById.get(rightId);
  const leftX = Number(left?.position?.x);
  const rightX = Number(right?.position?.x);
  if (Number.isFinite(leftX) && Number.isFinite(rightX) && leftX !== rightX) return leftX - rightX;
  return (indexById.get(leftId) || 0) - (indexById.get(rightId) || 0);
};

/**
 * A deterministic, dependency-free top-to-bottom layout for story graphs.
 * Cyclic links stay visible in React Flow, but are ignored as rank constraints.
 */
export function layoutStoryGraph(nodes, entryNodeId, options = {}) {
  const safeNodes = asNodes(nodes);
  if (safeNodes.length < 2) return safeNodes;

  const nodeWidth = Number(options.nodeWidth) || 320;
  const columnGap = Number(options.columnGap) || 108;
  const rowGap = Number(options.rowGap) || 96;
  const originY = Number(options.originY) || 80;
  const nodeById = new Map(safeNodes.map((node) => [node.id, node]));
  const indexById = new Map(safeNodes.map((node, index) => [node.id, index]));
  const compareOriginal = compareByOriginalPosition(indexById, nodeById);
  const rawChildren = new Map(safeNodes.map((node) => [node.id, []]));
  const rawIndegree = new Map(safeNodes.map((node) => [node.id, 0]));

  safeNodes.forEach((node) => {
    const targets = [];
    (Array.isArray(node.answers) ? node.answers : []).forEach((answer) => {
      const targetId = answer?.targetNodeId;
      if (!targetId || targetId === node.id || !nodeById.has(targetId) || targets.includes(targetId)) return;
      targets.push(targetId);
      rawIndegree.set(targetId, (rawIndegree.get(targetId) || 0) + 1);
    });
    rawChildren.set(node.id, targets);
  });

  const requestedEntryId = nodeById.has(entryNodeId) ? entryNodeId : safeNodes[0].id;
  const rootIds = [
    requestedEntryId,
    ...safeNodes
      .map((node) => node.id)
      .filter((id) => id !== requestedEntryId && rawIndegree.get(id) === 0)
      .sort(compareOriginal),
  ];
  const reachableIds = new Set();
  const reachableQueue = [requestedEntryId];
  for (let cursor = 0; cursor < reachableQueue.length; cursor += 1) {
    const nodeId = reachableQueue[cursor];
    if (reachableIds.has(nodeId)) continue;
    reachableIds.add(nodeId);
    (rawChildren.get(nodeId) || []).forEach((targetId) => {
      if (!reachableIds.has(targetId)) reachableQueue.push(targetId);
    });
  }

  const children = new Map(safeNodes.map((node) => [node.id, []]));
  const parents = new Map(safeNodes.map((node) => [node.id, []]));
  const visitState = new Map();

  const visit = (nodeId) => {
    visitState.set(nodeId, 1);
    (rawChildren.get(nodeId) || []).forEach((targetId) => {
      // A return-to-opening link is still rendered, but must not push the opening below another node.
      if (targetId === requestedEntryId && nodeId !== requestedEntryId) return;
      if (visitState.get(targetId) === 1) return;
      children.get(nodeId).push(targetId);
      parents.get(targetId).push(nodeId);
      if (!visitState.get(targetId)) visit(targetId);
    });
    visitState.set(nodeId, 2);
  };

  rootIds.forEach((rootId) => {
    if (!visitState.get(rootId)) visit(rootId);
  });
  safeNodes.forEach((node) => {
    if (!visitState.get(node.id)) {
      rootIds.push(node.id);
      visit(node.id);
    }
  });

  const indegree = new Map(safeNodes.map((node) => [node.id, parents.get(node.id).length]));
  const rankById = new Map(safeNodes.map((node) => [node.id, 0]));
  const queue = safeNodes
    .map((node) => node.id)
    .filter((id) => indegree.get(id) === 0)
    .sort((leftId, rightId) => {
      if (leftId === requestedEntryId) return -1;
      if (rightId === requestedEntryId) return 1;
      return compareOriginal(leftId, rightId);
    });

  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const nodeId = queue[cursor];
    (children.get(nodeId) || []).forEach((targetId) => {
      rankById.set(targetId, Math.max(rankById.get(targetId) || 0, (rankById.get(nodeId) || 0) + 1));
      indegree.set(targetId, indegree.get(targetId) - 1);
      if (indegree.get(targetId) === 0) queue.push(targetId);
    });
  }

  const layers = [];
  safeNodes.filter((node) => reachableIds.has(node.id)).forEach((node) => {
    const rank = rankById.get(node.id) || 0;
    if (!layers[rank]) layers[rank] = [];
    layers[rank].push(node.id);
  });
  layers.forEach((layer) => layer.sort(compareOriginal));

  const reorderLayer = (rank, neighborMap) => {
    const layer = layers[rank];
    if (!layer?.length) return;
    const orderById = new Map();
    layers.forEach((items) => items.forEach((id, index) => orderById.set(id, index)));
    layer.sort((leftId, rightId) => {
      const score = (id) => {
        const neighbors = neighborMap.get(id) || [];
        if (!neighbors.length) return null;
        return neighbors.reduce((sum, neighborId) => sum + (orderById.get(neighborId) || 0), 0) / neighbors.length;
      };
      const leftScore = score(leftId);
      const rightScore = score(rightId);
      if (leftScore != null && rightScore != null && leftScore !== rightScore) return leftScore - rightScore;
      if (leftScore != null && rightScore == null) return -1;
      if (leftScore == null && rightScore != null) return 1;
      return compareOriginal(leftId, rightId);
    });
  };

  for (let pass = 0; pass < 3; pass += 1) {
    for (let rank = 1; rank < layers.length; rank += 1) reorderLayer(rank, parents);
    for (let rank = layers.length - 2; rank >= 0; rank -= 1) reorderLayer(rank, children);
  }

  const slotWidth = nodeWidth + columnGap;
  const positionById = new Map();
  const estimateNodeHeight = (node) => {
    if (node?.nodeType === 'ending') return 112;
    const answerCount = Math.max(1, asNodes(node?.answers).length);
    return 150 + answerCount * 36;
  };
  const positionedLayers = layers.map((layer) => {
    const mainIds = layer.filter((nodeId) => nodeById.get(nodeId)?.trackType === 'main');
    if (!mainIds.length) return { nodeIds: layer, centerIndex: Math.max(0, layer.length - 1) / 2 };
    const branchIds = layer.filter((nodeId) => nodeById.get(nodeId)?.trackType !== 'main');
    const leftCount = Math.ceil(branchIds.length / 2);
    const nodeIds = [
      ...branchIds.slice(0, leftCount),
      ...mainIds,
      ...branchIds.slice(leftCount),
    ];
    return {
      nodeIds,
      centerIndex: leftCount + Math.max(0, mainIds.length - 1) / 2,
    };
  });
  const layerYByRank = [];
  let nextLayerY = originY;
  positionedLayers.forEach(({ nodeIds }, rank) => {
    layerYByRank[rank] = nextLayerY;
    const layerHeight = Math.max(112, ...nodeIds.map((nodeId) => estimateNodeHeight(nodeById.get(nodeId))));
    nextLayerY += layerHeight + rowGap;
  });

  let connectedHalfWidth = nodeWidth / 2;
  positionedLayers.forEach(({ nodeIds, centerIndex }, rank) => {
    nodeIds.forEach((nodeId, index) => {
      const x = (index - centerIndex) * slotWidth;
      positionById.set(nodeId, {
        x,
        y: layerYByRank[rank],
      });
      connectedHalfWidth = Math.max(connectedHalfWidth, Math.abs(x) + nodeWidth / 2);
    });
  });

  const parkingIds = safeNodes
    .map((node) => node.id)
    .filter((nodeId) => !reachableIds.has(nodeId))
    .sort(compareOriginal);
  const parkingColumns = Math.max(1, Math.min(4, Math.ceil(Math.sqrt(parkingIds.length))));
  const parkingStartX = connectedHalfWidth + slotWidth * 1.5;
  let parkingY = originY;
  for (let rowStart = 0; rowStart < parkingIds.length; rowStart += parkingColumns) {
    const rowIds = parkingIds.slice(rowStart, rowStart + parkingColumns);
    rowIds.forEach((nodeId, columnIndex) => {
      positionById.set(nodeId, {
        x: parkingStartX + columnIndex * slotWidth,
        y: parkingY,
      });
    });
    const rowHeight = Math.max(112, ...rowIds.map((nodeId) => estimateNodeHeight(nodeById.get(nodeId))));
    parkingY += rowHeight + rowGap;
  }

  return safeNodes.map((node) => ({
    ...node,
    position: positionById.get(node.id) || node.position,
  }));
}
